import { Tooltip } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import FinanceService from "Lib/API/Finance";
import { ReactComponent as AddIcon } from "assets/icons/add-plus.svg";
import BackSvgComponent from "assets/icons/svg/back";
import { InputField, MyCheckbox } from "components";
import ManageAchCards from "components/ManageAchCards/ManageAchCards";
import ManageCreditsCard from "components/ManageCreditsCard/ManageCreditsCard";
import MyButton from "components/MyButton";
import RadioButtons from "components/RadioButtons/RadioButtons";
import Recaptcha from "components/Recaptcha/Recaptcha";
import Button from "components/button/Button";
import { checkIsStripe, checkIsTranzila } from "helper/checkIsStripe";
import { numberFormat } from "helper/numberFormat";
import { convenienceFeeAmount as calcConvenienceFee } from "helper/convenienceFee";
import { postalCodeFormat, postalCodeLabel } from "helper/postalCodeFormat";
import useGetUserAccount from "hook/useGetUserAccount";
import { useSnackbar } from "notistack";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory, useLocation } from "react-router-dom";
import thousands from "thousands";
import { ReactComponent as ClockWiseRefresh } from "../../../assets/icons/clockWise_refresh.svg";
import StripeCard from "../../../components/StripeCard/StripeCard";
import TranzilaCard from "../../../components/TranzilaCard/TranzilaCard";
import DonationStepper from "../DonationStepper";
import { PaymentResultPage } from "../PaymentResultPage/PaymentResultPage";
import "./DonationPay.scss";
import { Fonts, FontWeights, FontColors } from "theme/fontConfig";

const DonationPay = ({
  formik,
  enableCoverConvenienceFee,
  setEnableCoverConvenienceFee,
  external,
  enableBillMyAccount,
  setSelectedAchCard,
  selectedAchCard,
  selectedCard,
  setSelectedCard,
  enableCreditCardPay,
  enableAchPay,
  disabledAchPay,
  displayAchTooltip,
  getCurrentPaymentMethod,
  paymentMethodDonation,
  achPaymentMethodDonation,
}) => {
  const addCardStripeRef = useRef(null);
  const recaptchaRef = useRef(null);

  const postalLabel = postalCodeLabel();

  const [error, setError] = useState(null);

  const [loading, setLoading] = useState(false);
  const [cardLoading, setCardLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [cardList, setCardList] = useState([]);
  const [addNewCard, setAddNewCard] = useState(false);
  const [newCardDetails, setNewCardDetails] = useState({});
  const [addCardLoading, setAddCardLoading] = useState(false);
  const [tranzilaExternalChargeResult, setTranzilaExternalChargeResult] = useState(null);
  const [tranzilaCurrency, setTranzilaCurrency] = useState("ILS");
  const [tranzilaConvertedAmount, setTranzilaConvertedAmount] = useState(null);
  // Treat logged-in members as authenticated even on the external donation page
  const isLoggedInMember = !!localStorage.getItem("accountID") && !!localStorage.getItem("token");
  const treatAsExternal = external && !isLoggedInMember;
  const { push } = useHistory();
  const { search } = useLocation();

  let accountID = localStorage.getItem("accountID")
    ? localStorage.getItem("accountID")
    : "";

  const accountData = useGetUserAccount({ member: !external });

  const currentPaymentMethod = useMemo(() => getCurrentPaymentMethod(), [
    getCurrentPaymentMethod,
  ]);

  const convenienceFee = useMemo(() => {
    return {
      coverConvenienceFee: currentPaymentMethod?.coverConvenienceFee || false,
      convenienceFeeInfo: currentPaymentMethod?.convenienceFeeInfo || {},
    };
  }, [currentPaymentMethod]);

  const handleAddNewCard = async (tokenId = "") => {
    setAddCardLoading(true);
    const token = localStorage.getItem("token");

    const cardsDetails = {
      object: "card",
      number: newCardDetails?.["cardDetails.number"],
      exp_month: newCardDetails?.["cardDetails.exp_month"],
      exp_year: newCardDetails?.["cardDetails.exp_year"],
      cvc: newCardDetails?.["cardDetails.cvc"],
      zip: newCardDetails?.["cardDetails.zip"],
    };
    if (!isStripe) {
      if (
        !cardsDetails?.number ||
        !cardsDetails?.exp_year ||
        !cardsDetails?.cvc
      ) {
        return enqueueSnackbar("please fill all mandatory fields", {
          variant: "error",
        });
      }

      if (!cardsDetails?.zip?.length) {
        return enqueueSnackbar(`Please enter ${postalCodeLabel()}.`, {
          variant: "error",
        });
      }

      const validZipCode = postalCodeFormat(cardsDetails?.zip);
      if (!validZipCode) {
        enqueueSnackbar(`Invalid ${postalLabel}`, { variant: "info" });
      }
    }
    const bodyData =
      isStripe && tokenId ? { token: tokenId } : { cardsDetails };

    FinanceService.addCreditCard(token, accountID, bodyData)
      .then((res) => {
        fetchCards();
        setAddNewCard(false);
        setNewCardDetails({});
      })
      .catch((err) => {
        enqueueSnackbar(err?.data?.message, { variant: "error" });
      })
      .finally(() => {
        setAddCardLoading(false);
      });
  };

  async function fetchCards() {
    setCardLoading(true);
    const token = localStorage.getItem("token");
    await FinanceService.getCreditCardsByAccountId(token, accountID)
      .then((res) => {
        setCardLoading(false);
        setCardList(res?.card?.data);
      })
      .catch((err) => {
        setCardLoading(false);
        enqueueSnackbar(err?.data?.message, { variant: "error" });
      });
  }
  useEffect(() => {
    if (!accountData) return;
    accountID = !external
      ? accountData?.user?.permissions?.linked_account?._id
      : "";
    if (!external) {
      fetchCards();
    }
    window.onload = function() {
      push(`/donation${search}`);
    };
    return () => {
      window.onload = null;
    };
  }, [accountData]);

  const handleGetStripeToken = async () => {
    const tokenId = await addCardStripeRef?.current?.handleGetToken();
    if (tokenId && tokenId !== null) {
      handleDonate(tokenId);
    }
  };

  // Convert donation amount to selected currency for Tranzila iframe
  const baseIlsAmount = +(formik?.values?.causeInfo?.reduce((acc, c) => acc + +c?.amount, 0) || 0) + (enableCoverConvenienceFee ? +convenienceFeeAmount : 0);
  // Conversion happens in backend public handshake — reset display when currency changes
  useEffect(() => {
    setTranzilaConvertedAmount(null);
  }, [tranzilaCurrency]);

  const tranzilaChargeAmount = baseIlsAmount;

  // Called when Tranzila iframe completes payment for external users
  const handleTranzilaCharged = (result) => {
    setTranzilaExternalChargeResult(result);
    handleDonate("", result);
  };

  const handleSubmitDonation = async () => {
    const captchaValue = recaptchaRef?.current?.getValue();
    if (!captchaValue && treatAsExternal)
      return enqueueSnackbar("Please verify the captcha", {
        variant: "info",
      });

    if (isStripe && treatAsExternal && formik.values?.activePayment === "credit-card") {
      handleGetStripeToken();
      return;
    }
    handleDonate();
  };

  const getLedgerSubLedger = (ledger) => {
    let Ledger;
    let SubLedger;
    if (ledger?.cause?.name) {
      Ledger = {
        id: ledger?.cause?.ledger_id,
        name: ledger?.cause?.name,
        account_receivable: ledger?.cause?.ledger_account_receivable,
      };
      SubLedger = {
        id: ledger?.cause?.sub_ledger_id,
        name: ledger?.cause?.sub_ledger_name,
        account_receivable: ledger?.cause?.sub_account_receivable,
      };
    } else {
      Ledger = {
        id: ledger?.cause?._id,
        name: ledger?.cause?.ledger_name,
        account_receivable: ledger?.cause?.account_receivable,
      };
    }

    return { Ledger, SubLedger };
  };

  const handleDonate = (tokenId = "", tranzilaChargeData = null) => {
    const token = localStorage.getItem("token"); // login users
    const externalToken = localStorage.getItem("externalToken"); // non login users

    if (localStorage.getItem("hasPayment") === "false") {
      enqueueSnackbar("This user cannot donate", { variant: "error" });
      return;
    }

    if (
      !treatAsExternal &&
      !selectedCard &&
      formik?.values?.activePayment === "credit-card"
    ) {
      enqueueSnackbar("Please select a credit card", { variant: "error" });
      return;
    }

    if (
      !treatAsExternal &&
      !selectedAchCard &&
      formik?.values?.activePayment === "ach"
    ) {
      enqueueSnackbar("Please select an ACH card", { variant: "error" });
      return;
    }

    // For Tranzila external: payment was handled by the iframe, skip card field validation
    const isTranzilaExternal = treatAsExternal && isTranzila && formik?.values?.activePayment === "credit-card";
    if (
      !isTranzilaExternal &&
      treatAsExternal &&
      formik?.values?.activePayment === "credit-card" &&
      !isStripe &&
      (!formik?.values?.card_number ||
        !formik?.values?.expiry ||
        !formik?.values?.cvc)
    ) {
      enqueueSnackbar("please fill all mandatory fields", { variant: "error" });
      return;
    }

    if (
      !isTranzilaExternal &&
      formik?.values?.activePayment === "credit-card" &&
      treatAsExternal &&
      !isStripe &&
      !formik?.values?.zip?.length
    ) {
      return enqueueSnackbar(`Please enter ${postalCodeLabel()}.`, {
        variant: "error",
      });
    }

    const validZipCode = postalCodeFormat(formik?.values?.zip);
    if (
      !isTranzilaExternal &&
      formik?.values?.activePayment === "credit-card" &&
      treatAsExternal &&
      !isStripe &&
      !validZipCode
    ) {
      enqueueSnackbar(`Invalid ${postalLabel}`, { variant: "info" });
    }

    const data = {
      isUser: !treatAsExternal ? true : false,
      accountDetails: !treatAsExternal
        ? undefined
        : {
            first_name: formik?.values?.first_name,
            last_name: formik?.values?.last_name,
            primary_email_address: formik?.values?.receipt_email,
            addresses: [
              {
                location: {
                  formattedAddress:
                    formik?.values?.addressInfo?.formattedAddress,
                  place_id: formik?.values?.addressInfo?.place_id,
                },
                city: formik?.values?.city,
                state: formik?.values?.state,
                zip_code: formik?.values?.zip_code,
                addressLine2: formik?.values?.address2 ?? undefined,
              },
            ],
          },
      Account: !treatAsExternal
        ? {
            id: accountID,
            name: localStorage.getItem("accountName"),
          }
        : undefined,
      organization: treatAsExternal ? localStorage.getItem("orgId") : undefined,
      // For Tranzila external: payment already done in iframe, skip cardsDetails
      cardsDetails:
        !isStripe && !isTranzila && treatAsExternal && formik?.values?.activePayment === "credit-card"
          ? {
              number: formik?.values?.card_number,
              exp_month: formik?.values?.expiry?.split("/")[0],
              exp_year: formik?.values?.expiry?.split("/")[1],
              cvc: formik?.values?.cvc,
              zip: formik?.values?.zip,
            }
          : undefined,
      token:
        treatAsExternal && isStripe && formik?.values?.activePayment === "credit-card"
          ? tokenId
          : undefined,
      tranzilaChargeResult: tranzilaChargeData || undefined,
      cardId: !treatAsExternal
        ? formik?.values?.activePayment === "credit-card"
          ? selectedCard
          : formik?.values?.activePayment === "ach"
          ? selectedAchCard
          : undefined
        : undefined,
      transactions: formik?.values?.causeInfo?.map((item, index) => ({
        Ledger: getLedgerSubLedger(item).Ledger,
        SubLedger: getLedgerSubLedger(item).SubLedger,
        isRecurring: item?.contribution?.isRecurring,
        frequency:
          item?.contribution?.isRecurring &&
          item?.contribution?.frequency === "yearly"
            ? "year"
            : item?.contribution?.isRecurring &&
              item?.contribution?.frequency === "monthly"
            ? "month"
            : undefined,
        charge_type: "donation",
        total_amount: item?.amount,
        note: item?.note ? item?.note : undefined,
      })),
      ...(formik?.values?.tributeCheck
        ? {
            tribute_info: {
              type: formik?.values?.tribute_type?.name,
              name: formik?.values?.donor_name,
              message: formik?.values?.note ? formik?.values?.note : undefined,
              acknowledgment: formik?.values?.showAcknowledgement,
              recipients: formik?.values?.showAcknowledgement
                ? formik?.values?.acknowledgements
                : undefined,
              sendPaper: formik?.values?.sendPaper,
              address: {
                location: {
                  formattedAddress:
                    formik?.values?.addressInfo?.formattedAddress,
                  place_id: formik?.values?.addressInfo?.place_id,
                },
                city: formik?.values?.city,
                state: formik?.values?.state,
                zip_code: formik?.values?.zip_code,
                addressLine2: formik?.values?.address2 ?? undefined,
              },
            },
          }
        : formik?.values?.sendPaper
        ? {
            tribute_info: {
              sendPaper: formik?.values?.sendPaper,
              address: {
                location: {
                  formattedAddress:
                    formik?.values?.addressInfo?.formattedAddress,
                  place_id: formik?.values?.addressInfo?.place_id,
                },
                city: formik?.values?.city,
                state: formik?.values?.state,
                zip_code: formik?.values?.zip_code,
                addressLine2: formik?.values?.address2 ?? undefined,
              },
            },
          }
        : {}),
      coverFee:
        formik?.values?.activePayment !== "bill-my-account"
          ? enableCoverConvenienceFee
          : false,
      billMyAccount: formik?.values?.activePayment === "bill-my-account",
      paymentMethodId:
        formik?.values?.activePayment === "credit-card"
          ? paymentMethodDonation?._id
          : formik?.values?.activePayment === "ach"
          ? achPaymentMethodDonation?._id
          : undefined,
    };
    const makeDonation = async () => {
      setLoading(true);
      await FinanceService.makeDonation(data, treatAsExternal ? externalToken : token)
        .then((res) => {
          setLoading(false);
          formik.resetForm();
          enqueueSnackbar("Donation Done successfully!", {
            variant: "success",
          });
          formik.setFieldValue({});
          formik.setFieldValue("resultPage", "success");
        })
        .catch((err) => {
          setLoading(false);
          enqueueSnackbar(err?.data?.message, { variant: "error" });
        });
    };
    makeDonation();
  };

  const handleExpiryChange = (text) => {
    formik.setFieldValue("expiry", text);
    if (text.length > 2) {
      if (text[2] !== "/") {
        formik.setFieldValue(
          "expiry",
          text.substring(0, 2) + "/" + text.substring(2, 4)
        );
      }
    }
  };
  const onCardDetailChange = (e) => {
    setNewCardDetails({
      ...newCardDetails,
      [e.target.name]: e.target.value,
    });
  };

  const calculateTotal = () => {
    const total = formik?.values?.causeInfo?.reduce(
      (acc, c) => acc + +c?.amount,
      0
    )
      ? thousands(
          (+formik?.values?.causeInfo?.reduce(
            (acc, c) => acc + +c?.amount,
            0
          )).toFixed(2)
        )
      : "0.00";
    return total;
  };
  const convenienceFeeAmount = enableCoverConvenienceFee
    ? calcConvenienceFee(
        convenienceFee?.convenienceFeeInfo,
        calculateTotal().replace(/,/g, ""),
      )
    : 0;

  const calculateTotalPayment = () => {
    const totalPayment = calculateTotal().replace(/,/g, "");
    if (
      enableCoverConvenienceFee &&
      formik?.values?.activePayment !== "bill-my-account"
    ) {
      return numberFormat({
        number: +totalPayment + +convenienceFeeAmount,
        decimal: 2,
        style: "currency",
      });
    } else {
      return numberFormat({
        number: +totalPayment,
        decimal: 2,
        style: "currency",
      });
    }
  };

  const isStripe = useMemo(() => checkIsStripe(currentPaymentMethod), [
    currentPaymentMethod,
  ]);
  const isTranzila = useMemo(() => checkIsTranzila(currentPaymentMethod), [
    currentPaymentMethod,
  ]);
  const checkIfAnyCauseRecurringPayment = useCallback(() => {
    const causes = formik.values?.causeInfo;
    if (!causes) return false;
    return causes.some((cause) => cause?.contribution?.isRecurring);
  }, [formik.values?.causeInfo]);

  const isRecurringPayment = useMemo(() => checkIfAnyCauseRecurringPayment(), [
    checkIfAnyCauseRecurringPayment,
  ]);

  const paymentTabs = [
    ...(enableCreditCardPay
      ? [
          {
            label: "Pay with credit card",
            value: "credit-card",
          },
        ]
      : []),
    ...(enableAchPay
      ? [
          {
            label: "Pay with ACH",
            value: "ach",
            disabled: disabledAchPay,
            showTooltip: displayAchTooltip,
            tooltip: displayAchTooltip,
            tooltipPlacement: "top",
          },
        ]
      : []),
    ...(enableBillMyAccount
      ? [
          {
            label: "Bill my account",
            value: "bill-my-account",
            disabled: isRecurringPayment,
            showTooltip: isRecurringPayment,
            tooltip: "Bill my account is not available for recurring donation",
          },
        ]
      : []),
  ];

  return (
    <>
      {formik.values?.resultPage !== "" ? (
        <div style={{ marginTop: 20, marginBottom: 20 }}>
          <PaymentResultPage
            result={formik.values?.resultPage}
            formik={formik}
            showThanksMsg={
              formik.values?.activePayment === "bill-my-account" || false
            }
            path={external ? "/donation" : "/donation-view"}
          />
        </div>
      ) : (
        <>
          <div className="donation__content">
            <DonationStepper activeStep={2} />
            <div className="DonationPay__content w-100">
              <Grid container>
                {paymentTabs?.length > 1 && (
                  <RadioButtons
                    value={formik?.values?.activePayment}
                    name={"activePayment"}
                    row={true}
                    onChange={(value) => {
                      formik.setFieldValue("activePayment", value);
                    }}
                    size={"small"}
                    options={paymentTabs}
                  />
                )}
                {formik?.values?.activePayment === "credit-card" ? (
                  <>
                    {!treatAsExternal ? (
                      <Grid item xs={12}>
                        <ManageCreditsCard
                          isStripe={isStripe}
                          isTranzila={isTranzila}
                          currentPaymentMethod={currentPaymentMethod}
                          setSelectedCard={setSelectedCard}
                          selectedCard={selectedCard}
                          publicKey={
                            currentPaymentMethod?.paymentGateway?.keys
                              ?.publicKey
                          }
                        />
                      </Grid>
                    ) : (
                      <>
                        {isTranzila ? (
                          <>
                            {/* Summary above iframe for Tranzila external */}
                            {formik?.values?.causeInfo?.map((item, idx) => (
                              <Grid item xs={12} key={idx}>
                                <div className="couses__amount d-flex align-items-center justify-content-between">
                                  <div>{item?.cause?.sub_ledger_name || item?.cause?.ledger_name}</div>
                                  <div>{numberFormat({ number: +item?.amount, decimal: 2, style: "currency" })}</div>
                                </div>
                              </Grid>
                            ))}
                            {enableCoverConvenienceFee && +convenienceFeeAmount > 0 && (
                              <Grid item xs={12}>
                                <div className="convenience__fee d-flex align-items-center justify-content-between">
                                  <div>Convenience fee</div>
                                  <div>{numberFormat({ number: convenienceFeeAmount, decimal: 2, style: "currency" })}</div>
                                </div>
                              </Grid>
                            )}
                            <Grid item xs={12}>
                              <div className="total__amount d-flex align-items-center justify-content-between" style={{ fontWeight: FontWeights.SemiBold, marginBottom: 12 }}>
                                <div>Total</div>
                                <div>{calculateTotalPayment()}</div>
                              </div>
                            </Grid>
                            <Grid item xs={12}>
                              <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                                {['ILS', 'USD'].map((cur) => (
                                  <button
                                    key={cur}
                                    type="button"
                                    onClick={() => setTranzilaCurrency(cur)}
                                    style={{
                                      padding: '4px 12px',
                                      borderRadius: 6,
                                      border: tranzilaCurrency === cur ? '2px solid #2555EF' : '1px solid #e2e8f0',
                                      background: tranzilaCurrency === cur ? '#EEF2FF' : '#fff',
                                      color: tranzilaCurrency === cur ? '#2555EF' : '#333',
                                      fontWeight: tranzilaCurrency === cur ? FontWeights.SemiBold : FontWeights.Regular,
                                      cursor: 'pointer',
                                    }}
                                  >
                                    {cur === 'ILS' ? '₪ ILS' : '$ USD'}
                                  </button>
                                ))}
                                {tranzilaCurrency === 'USD' && tranzilaConvertedAmount && (
                                  <span style={{ color: FontColors.Gray555555, fontSize: Fonts.GeneralBodyFont.fontSize }}>
                                    ≈ ${tranzilaConvertedAmount.toFixed(2)} USD
                                  </span>
                                )}
                              </div>
                              {(
                              <TranzilaCard
                                onSaveCard={() => {}}
                                onCharged={handleTranzilaCharged}
                                loading={loading}
                                setLoading={setLoading}
                                saveCard={false}
                                currency={tranzilaCurrency}
                                amount={tranzilaChargeAmount}
                                terminalName={currentPaymentMethod?.paymentGateway?.keys?.terminal_name}
                                email={formik?.values?.receipt_email}
                                contactName={`${formik?.values?.first_name || ""} ${formik?.values?.last_name || ""}`.trim()}
                                paymentMethodId={paymentMethodDonation?._id}
                                orgId={localStorage.getItem("orgId")}
                                onHandshake={({ convertedAmount }) => setTranzilaConvertedAmount(convertedAmount)}
                              />
                              )}
                            </Grid>
                          </>
                        ) : isStripe ? (
                          <Grid item xs={12}>
                            <StripeCard
                              loading={loading}
                              setLoading={setLoading}
                              saveCard={false}
                              ref={addCardStripeRef}
                              publicKey={
                                currentPaymentMethod?.paymentGateway?.keys
                                  ?.publicKey
                              }
                            />
                          </Grid>
                        ) : (
                          <>
                            <Grid item xs={12}>
                              <div className="card_num_field">
                                <InputField
                                  title="Card Number"
                                  mandatory
                                  type="string"
                                  minLength="16"
                                  maxLength="16"
                                  {...{
                                    // formik,
                                    name: "card_number",
                                    placeholder: "Card Number",
                                    value: formik?.values?.card_number,
                                  }}
                                  onKeyPress={(event) => {
                                    if (!/[0-9]/.test(event.key)) {
                                      event.preventDefault();
                                    }
                                  }}
                                  handleChange={(e) => {
                                    formik.setFieldValue(
                                      "card_number",
                                      e?.target?.value
                                    );
                                  }}
                                  inputClassName="donation__input"
                                />
                              </div>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <div className="exp_pay_field">
                                <InputField
                                  title="Expiry"
                                  mandatory
                                  type="string"
                                  maxLength="7"
                                  {...{
                                    // formik,
                                    name: "expiry",
                                    placeholder: "Expiry (MM/YY)",
                                    value: formik?.values?.expiry,
                                  }}
                                  onKeyPress={(event) => {
                                    if (!/[0-9]/.test(event.key)) {
                                      event.preventDefault();
                                    }
                                  }}
                                  handleChange={(e) =>
                                    handleExpiryChange(e?.target?.value)
                                  }
                                  inputClassName="donation__input"
                                />
                              </div>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <div className="cvc_pay_field">
                                <InputField
                                  title="CVC"
                                  mandatory
                                  type="string"
                                  minLength="3"
                                  maxLength="4"
                                  {...{
                                    // formik,
                                    name: "cvc",
                                    placeholder: "3-4 digits",
                                    value: formik?.values?.cvc,
                                  }}
                                  onKeyPress={(event) => {
                                    if (!/[0-9]/.test(event.key)) {
                                      event.preventDefault();
                                    }
                                  }}
                                  handleChange={(e) => {
                                    formik.setFieldValue(
                                      "cvc",
                                      e?.target?.value
                                    );
                                  }}
                                  inputClassName="donation__input"
                                />
                              </div>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <div className="zip_pay_field">
                                <InputField
                                  title={postalLabel}
                                  mandatory
                                  type="string"
                                  {...{
                                    // formik,
                                    name: "Zip",
                                    placeholder: "12345",
                                    value: formik?.values?.zip,
                                  }}
                                  handleChange={(e) => {
                                    formik.setFieldValue(
                                      "zip",
                                      e?.target?.value
                                    );
                                  }}
                                  inputClassName="donation__input"
                                />
                              </div>
                            </Grid>
                          </>
                        )}
                      </>
                    )}
                  </>
                ) : formik?.values?.activePayment === "ach" ? (
                  <Grid item xs={12} className="d-flex align-items-center">
                    <ManageAchCards
                      setSelectedAchCard={setSelectedAchCard}
                      selectedAchCard={selectedAchCard}
                    />
                  </Grid>
                ) : null}

                {convenienceFee.coverConvenienceFee &&
                  formik?.values?.activePayment !== "bill-my-account" && (
                    <Grid item xs={12}>
                      <div
                        style={{
                          height: "48px",
                          marginTop: "15px",
                        }}
                      >
                        <label
                          htmlFor="coverConvenienceFee"
                          className="d-flex align-items-center h-100 checkbox__label"
                          style={{
                            fontSize: Fonts.GeneralLabelFont.fontSize,
                          }}
                        >
                          <MyCheckbox
                            handlechange={(e) =>
                              setEnableCoverConvenienceFee(e?.target?.checked)
                            }
                            checked={enableCoverConvenienceFee}
                            name="checkboxHonor"
                            id="coverConvenienceFee"
                          />
                          I’d like to pay the convenience fee so that the
                          synagogue gets 100% of my payment
                        </label>
                      </div>
                    </Grid>
                  )}
                {/* summary — hidden for Tranzila external (shown above iframe instead) */}
                {!(treatAsExternal && isTranzila && formik?.values?.activePayment === "credit-card") && (
                <Grid item xs={12}>
                  <div className="summaryPayText">Summary</div>
                </Grid>)}
                {!(treatAsExternal && isTranzila && formik?.values?.activePayment === "credit-card") && formik?.values?.causeInfo?.map((item, idx) => (
                  <>
                    <Grid item xs={12} key={idx}>
                      <div className="couses__amount d-flex align-items-center justify-content-between">
                        <div>
                          {item?.cause?.sub_ledger_name ||
                            item?.cause?.ledger_name}
                        </div>
                        <div className="d-flex align-items-center">
                          {item?.contribution?.isRecurring && (
                            <span className="recurring_value">
                              <ClockWiseRefresh className="mr-1" /> Recurring{" "}
                              {item?.contribution?.frequency}
                            </span>
                          )}
                          {numberFormat({
                            number: +item?.amount,
                            decimal: 2,
                            style: "currency",
                          })}
                        </div>
                      </div>
                    </Grid>
                  </>
                ))}
                {!(treatAsExternal && isTranzila && formik?.values?.activePayment === "credit-card") && enableCoverConvenienceFee &&
                  formik?.values?.activePayment !== "bill-my-account" &&
                  +convenienceFeeAmount > 0 && (
                    <Grid item xs={12}>
                      <div className="convenience__fee d-flex align-items-center justify-content-between">
                        <div>Convenience fee</div>
                        <div className="d-flex align-items-center">
                          {numberFormat({
                            number: convenienceFeeAmount,
                            decimal: 2,
                            style: "currency",
                          })}
                        </div>
                      </div>
                    </Grid>
                  )}
                {!(treatAsExternal && isTranzila && formik?.values?.activePayment === "credit-card") && (
                  <>
                    <hr style={{ width: "100%", color: FontColors.Slate404253, marginTop: "25px" }} />
                    <Grid item xs={12}>
                      <div className="total__amount d-flex alien-items-center justify-content-between">
                        <div>Total</div>
                        <div>{calculateTotalPayment()}</div>
                      </div>
                    </Grid>
                  </>
                )}
              </Grid>
            </div>
            {treatAsExternal && !(isTranzila && formik?.values?.activePayment === "credit-card") && (
              <div className="donation-pay-buttons mt-5">
                <Recaptcha recaptchaRef={recaptchaRef} />
              </div>
            )}
            <div className="donation-pay-buttons">
              <Button
                className=" prev__btn"
                onClick={() => {
                  push(
                    external
                      ? `/donation/details${search}`
                      : `/donation-view/details${search}`
                  );
                }}
                variant="primary-outline"
                size="md"
              >
                Previous
              </Button>
              {!(treatAsExternal && isTranzila && formik?.values?.activePayment === "credit-card") && (
                <Button
                  className=" donate__btn"
                  onClick={handleSubmitDonation}
                  disabled={loading}
                  style={{ width: "auto" }}
                  variant="primary"
                  size="md"
                >
                  {formik.values?.activePayment === "bill-my-account"
                    ? "Bill My Account"
                    : "Donate"}
                  {loading && (
                    <span className="mx-3 spinner spinner-white"></span>
                  )}
                </Button>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default DonationPay;
